import { useEffect, useState } from 'react';
import Calc from '@src/Tools/Utils/Calc';
import { CONFIG } from '@src/App/Config/constants';
import { authFetch } from '../useFetch';
import { PromotionState, PromotionStatus } from './usePromotion.types';

const REFRESH_INTERVAL_MS = 2 * 60 * 1000;

const INACTIVE: PromotionState = {
	loaded: false,
	active: false,
	upcoming: false,
	banner: false,
	bonusPercent: 0,
	minAmount: 0,
	startsAt: null,
	endsAt: null,
};

let current: PromotionState = INACTIVE;
const listeners = new Set<(state: PromotionState) => void>();

const isPromotionStatus = (value: unknown): value is PromotionStatus => {
	const status = value as Partial<PromotionStatus> | null;
	return (
		typeof status?.active === 'boolean' &&
		typeof status?.bonusPercent === 'number' &&
		typeof status?.minAmount === 'number'
	);
};

export const refreshPromotion = async () => {
	let next: PromotionState = { ...current, loaded: true };
	try {
		//? Always bypass the HTTP cache so every load reflects the current HQ config
		const { json } = await authFetch(`${CONFIG.SERVER}/endpoints/giftcard/promotion?_=${Date.now()}`, {
			base: false,
			cache: 'no-store',
			headers: { 'Cache-Control': 'no-cache' },
		});
		if (isPromotionStatus(json)) next = { ...json, banner: json.banner === true, loaded: true };
	} catch (e) {
		console.error('Failed to load the gift card promotion status', e);
	}

	const changed = JSON.stringify(next) !== JSON.stringify(current);
	current = next;
	if (changed) listeners.forEach(listener => listener(current));
};

//? Keeps the status fresh, mount once at the app root
export const usePromotionSync = () => {
	useEffect(() => {
		refreshPromotion();
		const onFocus = () => document.visibilityState === 'visible' && refreshPromotion();
		const timer = setInterval(refreshPromotion, REFRESH_INTERVAL_MS);
		document.addEventListener('visibilitychange', onFocus);
		return () => {
			clearInterval(timer);
			document.removeEventListener('visibilitychange', onFocus);
		};
	}, []);
};

const usePromotion = () => {
	const [state, setState] = useState(current);

	useEffect(() => {
		listeners.add(setState);
		setState(current);
		return () => {
			listeners.delete(setState);
		};
	}, []);

	return state;
};

//? The minimum is checked against the whole order, not against each card
export const isPromotionEligible = (orderTotal: number, promotion: PromotionStatus): boolean =>
	promotion.active && orderTotal >= promotion.minAmount;

//? Mirrors the server's calculation, which is the one that decides the real value
export const getPromotionValue = (
	price: number,
	promotion: PromotionStatus,
	orderTotal: number
): number | null => {
	if (!isPromotionEligible(orderTotal, promotion)) return null;
	return Calc.floor(Calc.mul(price, Calc.add(1, Calc.div(promotion.bonusPercent, 100))));
};

export default usePromotion;
